import { create } from "zustand";
import type { SavedView, UnknownRecord } from "../types/common";
import { api } from "../api/client";

interface ViewsState {
  byId: Record<string, SavedView>;
  loading: boolean;
  error: string | null;
  loadForModule: (module: string) => Promise<void>;
  loadAll: () => Promise<void>;
  saveView: (module: string, viewName: string, viewConfig: UnknownRecord, description?: string, isDefault?: boolean) => Promise<SavedView>;
  updateView: (viewId: string, patch: { view_name?: string; description?: string; view_config?: UnknownRecord; is_default?: boolean }) => Promise<void>;
  deleteView: (viewId: string) => Promise<void>;
}

export const useViewsStore = create<ViewsState>((set, get) => ({
  byId: {},
  loading: false,
  error: null,

  loadForModule: async (module) => {
    set({ loading: true, error: null });
    try {
      const envelope = await api.get<{ views: SavedView[] }>(
        `/views?module=${encodeURIComponent(module)}&include_config=true`,
      );
      const rows = envelope.views ?? [];
      const byId: Record<string, SavedView> = { ...get().byId };
      for (const v of rows) byId[String(v.view_id)] = v;
      set({ byId, loading: false });
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : "Failed to load views",
      });
    }
  },

  loadAll: async () => {
    set({ loading: true, error: null });
    try {
      const envelope = await api.get<{ views: SavedView[] }>(
        `/views?include_config=true`,
      );
      const rows = envelope.views ?? [];
      const byId: Record<string, SavedView> = {};
      for (const v of rows) byId[String(v.view_id)] = v;
      set({ byId, loading: false });
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : "Failed to load views",
      });
    }
  },

  saveView: async (module, viewName, viewConfig, description, isDefault) => {
    const result = await api.post<SavedView>("/views", {
      view_name: viewName,
      description: description ?? null,
      view_config: viewConfig,
      is_default: isDefault ?? false,
      module,
    });
    await get().loadForModule(module);
    return result;
  },

  updateView: async (viewId, patch) => {
    await api.put(`/views/${viewId}`, patch);
    const existing = get().byId[viewId];
    if (existing) {
      const updated: SavedView = {
        ...existing,
        view_name: patch.view_name ?? existing.view_name,
        description: patch.description !== undefined ? patch.description : existing.description,
        view_config: patch.view_config ?? existing.view_config,
        is_default: patch.is_default !== undefined ? patch.is_default : existing.is_default,
      };
      // If setting as default, unset others in same module
      const byId = { ...get().byId };
      if (patch.is_default === true) {
        for (const v of Object.values(byId)) {
          if (v.module === existing.module && v.view_id !== viewId) {
            byId[String(v.view_id)] = { ...v, is_default: false };
          }
        }
      }
      byId[viewId] = updated;
      set({ byId });
    }
  },

  deleteView: async (viewId) => {
    await api.delete(`/views/${viewId}`);
    const { byId } = get();
    const next = { ...byId };
    delete next[viewId];
    set({ byId: next });
  },
}));
